import { FIXTURE_ID_PATTERN } from "./app-admin-inputs.js";
import { podPhaseView } from "./pod-phase.js";
import { podProblemEnum } from "./app-pods.js";
import { reconcileRefusalCodeSchema, reconcileRefusalOwnerSchema } from "./reconcile-refusal.js";
import { withholdReasonSchema } from "./app-diagnostics.js";
import { appEventSchema } from "./app-events.js";
import { appLogAggregateSchema } from "./app-logs.js";
import { z } from "zod";
//#region src/app-problems.ts
/**
* App-scoped "what is wrong right now" rollup —
* `GET /v1/admin/apps/:appId/dps/:dpId/problems`, and the `get_app_problems`
* MCP tool (spine **V-3**).
*
* ## ⛔ WHY THIS SHAPE EXISTS: `get_app_status` is DELETED and stays deleted (C-1)
*
* `get_app_status` rendered a structurally-empty backend as *"No recent
* logs/metrics/traces for this app in the last hour"* — telling an agent the app
* was **quiet** when in truth **nothing had been read**. It was deleted for that,
* and this file is the design that makes the same failure **inexpressible** rather
* than merely discouraged:
*
*  1. Every section is a DISCRIMINATED UNION on `read`. On the `read: false` arm
*     there is **no `data` key at all** — not an empty one. A consumer cannot
*     reach an empty array without first asserting that the read happened, so
*     "nothing" and "nothing was read" are not the same *type*, let alone the
*     same value.
*  2. There is **no** aggregate `health`, `ok`, `status`, `verdict` or
*     `problemCount` anywhere in this file, and the response is `.strict()`, so a
*     future edit that adds one is a hard parse failure at every consumer.
*  3. A type-level witness ({@link _appProblemsCarriesNoScalarVerdict}) makes the
*     absence a COMPILE error to undo, because a `.strict()` schema alone would
*     still let someone add the key deliberately.
*
* ⛔ **Do not add a count.** It is the same lie with arithmetic in front of it:
* `problemCount: 0` computed across sections where two were unread reads as "your
* app is fine". A caller who wants a count writes `data.items.length`, which is
* only reachable after narrowing on `read: true` — and the narrowing IS the
* honesty.
*/
/**
* Why a section carries no data. A CLOSED enum, because the whole value of this
* response is that "unread" is a first-class answer a client can branch on —
* a free-text reason would be prose an agent has to pattern-match.
*
* The FIVE values are exactly what the producer can genuinely DETERMINE:
*
* - **`read_failed`** — the section's read did not complete. ⛔ This is NOT an
*   empty result. The correlation `ref` joins it to the site-admin-api log, where
*   the cause is recorded in full.
* - **`not_deployed`** — this data plane has not reported this app as deployed, so
*   the read was deliberately not attempted. ⚠️ Pods may nonetheless EXIST: if a
*   deploy is in flight, or the operator's status report has not landed, the app
*   is running and unobserved. That is why this is `read: false` and not an empty
*   success.
* - **`no_desired_state`** — the data plane has no platform channel pinned, so a
*   desired state has never been computed for it. Nothing is known about what it
*   would withhold, which is not the same as nothing being withheld.
* - **`no_log_streams`** — ⭐ **R-39.2.** The aggregate's selector matched **no log
*   streams at all** on this data plane in this window. ⛔ This is NOT "the app was
*   quiet": it is equally consistent with a collector that stopped shipping for this
*   app, a Loki tenant with no streams, or pods renamed past a label change. It is a
*   `read: false` arm precisely BECAUSE the alternative rendering — `read: true,
*   total: 0` — is C-1 verbatim, inside the section built to replace C-1's tool.
*   ⚠️ It is distinguishable at the producer and nowhere else: R-1(3)'s instant query
*   returns an **empty vector** when nothing matched and a vector carrying `0` when
*   streams matched and counted zero lines. `LokiAggregateResult.matchedStreams`
*   is that one bit, and without it the two are the same value.
*
* ## ⚠️ RESIDUAL, stated rather than papered over: this is NOT a transport taxonomy
*
* `read_failed` deliberately does not separate *unreachable* from *non-2xx* from
* *malformed 200*, and that is a limit of the PRODUCER, not a shortcut here.
* Measured: all three adapters collapse the distinction before the rollup can
* observe it — `PodsProxyClientImpl` and `AppEventsProxyClientImpl` raise
* `BadGatewayException` for every non-2xx and for an unparseable body alike
* (`pods-proxy-failure.ts:70-82` composes one string for two causes;
* `describeMalformedPodsResponse` a third), and `AppDiagnosticsService.readWithheld`
* flattens `WithheldFetchError`'s four causes into one `errorstate` + `detail`
* string (`app-diagnostics.service.ts:162-172`). Recovering the distinction would
* mean widening those adapters to throw typed errors carrying a discriminator —
* a change to the error path of FOUR shipped endpoints, which spine V-3 does not
* authorise ("zero new data paths"). The `ref` carries the specifics to an
* operator; the enum carries what a client can act on.
*
* - **`not_reported`** — ⭐ **compute-classes D52(b).** This data plane's operator has reported no
*   compute resolutions: either it predates the field entirely (an un-upgraded DP) or it reported and
*   carried none. ⛔ This is NOT `read_failed` — nothing failed; the read succeeded and the DP simply
*   has not told us. And it is NOT `not_deployed`: the app may be deployed and reconciling fine here,
*   with only the PROVENANCE unavailable. Rendering either of those instead would attribute a
*   platform-version gap to the app.
*/
const PROBLEM_UNREAD_REASONS = [
	"read_failed",
	"not_deployed",
	"no_desired_state",
	"no_log_streams",
	"not_reported"
];
const problemUnreadReasonSchema = z.enum(PROBLEM_UNREAD_REASONS);
/**
* The static explanation for each unread reason — the `WITHHOLD_REMEDY` /
* `WITHHOLD_SCOPE` pattern (`app-diagnostics.ts:119,190`), for the same reason.
*
* ⭐ **Static, and NOT sent on the wire.** The prose is a property of the REASON,
* not of the response, so it lives in one place and cannot drift between
* occurrences. It keeps the wire arm exactly the `{read, reason, ref}` triple
* spine V-3 specifies, and it is the text `get_app_problems`' own tool
* description quotes so an agent gets it without a second call.
*
* ⛔ A `Record<ProblemUnreadReason, string>` and not a `switch`: the key set IS
* the mapping, so `app-problems.spec.ts` can assert completeness and a SIXTH
* reason cannot ship without its explanation. (R-39.2 added the fourth,
* `no_log_streams`; compute-classes D52(b) added the fifth, `not_reported`. TEST-C-2 is what made
* adding their text unskippable BOTH times — which is why it is a GENERIC key-set + length gate and
* must stay one: an enumerated list would have to be edited to catch the next member, and that is
* exactly the edit someone skips.)
*/
const PROBLEM_UNREAD_EXPLANATION = {
	read_failed: "The read for this section did not complete. This section is INCOMPLETE, not empty — do not conclude that nothing is wrong. The ref joins this answer to the site-admin-api log, where the cause is recorded in full.",
	not_deployed: "This data plane has not reported this app as deployed, so this section was not read. Pods may nonetheless exist: if a deploy is in flight, or the operator has not yet reported, the app is running and unobserved. Check get_app_diagnostics for whether the app is withheld from this data plane and why.",
	no_desired_state: "This data plane has no platform channel pinned, so a desired state has never been computed for it. Nothing is known about what it would withhold — which is not the same as nothing being withheld.",
	no_log_streams: "No log streams matched this app on this data plane in this window. This is NOT evidence the app was quiet — it is equally consistent with a log pipeline that is not shipping for this app. Check get_app_pods for whether pods exist, and get_app_diagnostics for whether the app is deployed here at all.",
	not_reported: "This data plane has reported no compute resolutions, so this section was not read. Either its operator predates the field or it reported and carried none — the app may be deployed and healthy here, with only the resolution provenance unavailable. Check get_app_diagnostics for whether the app is deployed on this data plane at all."
};
/**
* One section of the rollup, carrying its own provenance as a DISCRIMINATED
* UNION on `read`.
*
* ## ⛔ THIS IS DELIBERATELY NOT `app-diagnostics.ts`'s `sectionOf`, AND THE
* ## DIFFERENCE IS THE ENTIRE POINT OF V-3. DO NOT "ALIGN" THEM.
*
* `sectionOf` (`app-diagnostics.ts:395-406`) is
* `{status: 'collected'|'notcollected'|'errorstate', items: T[], detail: string|null}`
* — and its own docblock says `items` *"is always an array so a consumer can
* render it without narrowing"*. That was the right trade for a UI section, and
* it is the WRONG one for an agent surface: `items: []` is reachable on an
* `errorstate`, so the shape a careless consumer reads first — the array — is
* byte-identical between "checked, nothing wrong" and "the read failed". The
* three-state `status` makes the truth AVAILABLE; it does not make the lie
* UNAVAILABLE.
*
* ⇒ Here `data` is **absent** on the `read: false` arm. TypeScript will not let a
* consumer touch `section.data` without narrowing on `section.read === true`, so
* the C-1 failure is a compile error at every consumer rather than a discipline
* anyone has to remember. `PROBLEM_UNREAD_EXPLANATION` recovers everything
* `detail` was carrying, statically.
*
* ⚠️ The three-state → two-state collapse loses NOTHING, because the third state
* moved into the reason: `errorstate` is `read_failed`, and `notcollected`'s two
* causes are `not_deployed` and `no_desired_state` — which is strictly MORE
* information than one `notcollected` bucket.
*
* ⚠️ A generic factory, mirroring `sectionOf`, deliberately: the five sections
* differ only in `data`, and five longhand copies of a five-line union is the
* duplicated-invariant shape #1107 collapsed elsewhere. The house precedent for a
* generic schema factory in this package is `sectionOf` itself.
*/
const problemSection = (data) => z.discriminatedUnion("read", [z.object({
	read: z.literal(true),
	data
}).strict(), z.object({
	read: z.literal(false),
	reason: problemUnreadReasonSchema,
	/**
	* Opaque correlation id, ALWAYS present, echoed to the caller and written
	* to the site-admin-api log beside the cause.
	*
	* ⛔ Non-nullable even for `not_deployed` / `no_desired_state`, where
	* there is no failure to join. Two reasons: the union stays one shape (a
	* nullable field is a fourth state a consumer has to handle), and an
	* operator asked *"why did this agent see not_deployed at 03:12"* has a
	* log line to find. The service logs one line for EVERY `read: false`, not
	* only the failures — a ref with nothing behind it is worse than no ref.
	*
	* ⛔ NOT derived from any upstream response (RULE 2 of the proxy-failure
	* modules): an upstream-derived id would be upstream detail crossing the
	* boundary.
	*/
	ref: z.string().min(1)
}).strict()]);
/**
* Server-fixed per-section caps. ⛔ NOT caller-supplied: a rollup with a
* caller-tunable limit is a rollup an agent can narrow itself into a false
* all-clear with.
*
* Each is deliberately far BELOW the underlying service's own cap
* (`AppPodsService.MAX_PODS = 500`, `MAX_EVENT_LIMIT = 200`), because the job of
* this tool is a small answer an agent can hold in context — the field report's
* 143 KB response is the failure it exists to avoid. The full lists are one call
* away in `get_app_pods` / `get_app_events`, which the description names.
*/
const MAX_PROBLEM_PODS = 50;
const MAX_PROBLEM_EVENTS = 50;
const MAX_PROBLEM_WITHHELD = 50;
/** ⛔ Server-fixed, like its three siblings — NOT caller-supplied. A refusal list an agent can
*  narrow itself into a false all-clear with is the failure this whole module exists to avoid.
*  Sized well below `COMPUTE_RESOLUTION_MAX` (200): this tool's job is a small answer an agent can
*  hold in context, and a DP-wide 200-row array is the 143 KB response the field report complained
*  about. The full list is one `get_app_diagnostics` call away. */
const MAX_PROBLEM_COMPUTE_REFUSALS = 25;
/**
* The `errorRate` section's window, in minutes. Server-fixed, like the caps.
*
* ⚠️ **60 minutes matches `AppLogsService`'s own default window**
* (`app-logs.service.ts:167` — `now − 3600_000`), deliberately: an agent that
* reads a count here and then calls `get_app_logs` with no `start` gets lines from
* the SAME window, so the count and the lines are about the same period. Two
* different implicit windows on two tools an agent uses in sequence is the kind of
* quiet disagreement nobody notices until the numbers do not add up.
*/
const PROBLEM_ERROR_RATE_WINDOW_MINUTES = 60;
/**
* One pod with at least one derived problem.
*
* ⚠️ A projection of `AppPodSummary`, NOT the whole thing: `containers[]`,
* `attribution`, `nodeName` and `ageSeconds` are omitted because an agent asking
* "what is wrong" does not need them and they are the bulk of the bytes.
* `get_app_pods` returns the full shape.
*
* ⛔ `problems` reuses `podProblemEnum` and does NOT define its own vocabulary.
* The reason is measured and recorded at `app-diagnostics.ts:303-341`: an earlier
* parallel SCREAMING_CASE mapping was **one row short** —
* `CreateContainerConfigError` had no target and would have been dropped in
* silence, so `get_app_pods` would show a problem on the same data where this
* tool reported nothing wrong. Sharing the enum means the next problem
* `derivePodProblems` learns to derive arrives here for free instead of vanishing.
*/
const appProblemPodSchema = z.object({
	name: z.string(),
	/** `null` is a real answer — a legitimately-scoped pod can carry no fixture. */
	fixtureId: z.string().nullable(),
	versionSlug: z.string().nullable(),
	/**
	* The k8s pod phase.
	*
	* ⛔ **NO LONGER "verbatim" (#1994).** It is {@link podPhaseView}: recognised values pass
	* through unchanged and an unrecognised one degrades to `'Unknown'` rather than rejecting the
	* row. A missing key, a `null` and a non-string are still REJECTED.
	*/
	phase: podPhaseView,
	restarts: z.number().int().nonnegative(),
	/**
	* De-duplicated, in `POD_PROBLEMS` declaration order.
	*
	* ⛔ `.min(1)` — a pod with NO problems is not in this list at all. An entry
	* with an empty `problems` array would be a row that says "this pod is
	* broken" and then declines to say how.
	*/
	problems: z.array(podProblemEnum).min(1)
}).strict();
/**
* The `pods` section's data.
*
* ⭐ **`items: []` WITH `read: true` is the one legitimate clean bill of health on
* this whole surface**, and it is legitimate precisely because `read: true` is
* asserted alongside it: it means *checked, nothing wrong*. That claim is only
* safe because the alternative — not checked — is a different arm of the union
* and cannot produce this value.
*/
const podsProblemDataSchema = z.object({
	/**
	* ⚠️ **The first {@link MAX_PROBLEM_PODS} BY POD NAME, not the worst.** WS-1
	* makes `get_app_pods` return its list SORTED BY NAME (its one deliberate
	* observable behaviour change), so the service's `.slice(0, MAX_PROBLEM_PODS)`
	* yields the alphabetically first 50 problem pods. Deterministic is an
	* improvement over the previous arbitrary order, but the tool is sold as
	* *"the worst N things right now"* and an agent will read a cap as a severity
	* ranking. ⛔ The tool description says "the first 50 by pod name, not by
	* severity" for exactly this reason; do not drop that clause as redundant.
	*/
	items: z.array(appProblemPodSchema),
	/**
	* TRUE ⇒ there may be more problem pods than are listed. TWO independent
	* grounds, both needed — the same two-grounds construction as
	* `AppEventsService.isTruncated` (`app-events.service.ts:122-124`):
	*
	*  1. the problem list exceeded {@link MAX_PROBLEM_PODS} (compared EXACTLY,
	*     `> cap`, never `length === cap`: an exactly-full page is the commonest
	*     honest answer and flagging it trains callers to ignore the field);
	*  2. the UNDERLYING pod list came back truncated (`AppPodsResponse.truncated`
	*     — the DP has more than `AppPodsService.MAX_PODS` pods in this app's
	*     scope), in which case a problem pod may exist beyond it and the CP
	*     cannot prove otherwise.
	*
	* ⛔ Ground 2 is the one that is easy to leave out, and leaving it out is the
	* #880 failure — `truncated: false` while rows were cut upstream.
	*/
	truncated: z.boolean()
}).strict();
/** The `warningEvents` section's data. */
const warningEventsProblemDataSchema = z.object({
	/**
	* `type: 'Warning'` events only, newest first, verbatim `AppEvent` rows.
	*
	* ⚠️ `Normal` events are excluded by construction, not filtered by the
	* caller — this is the "what is wrong" tool, and a Normal event is by
	* definition not that. `get_app_events` serves both.
	*/
	items: z.array(appEventSchema),
	/**
	* Passed through from `AppEventsResponse.truncated` UNTOUCHED. It already
	* carries the two grounds documented at `app-events.service.ts:102-124`
	* (filtered-more-than-returned, and the upstream DP fetch came back full).
	* ⛔ Do not recompute it here and do not default it.
	*/
	truncated: z.boolean()
}).strict();
/** The `rolloutWithheld` section's data. */
const rolloutWithheldProblemDataSchema = z.object({
	/**
	* Full `WithholdReason` rows — `code`, `scope`, `fixtureId`, `detail`,
	* `remedy`, and `configKey` where the code carries one.
	*
	* ⭐ The ONE section that is not projected down, deliberately: `remedy` is the
	* whole product value of a withholding reason (it is the next action), and
	* `detail` already has the concrete identifiers substituted in. Dropping
	* either would leave an agent knowing its app is withheld and not what to do.
	*/
	items: z.array(withholdReasonSchema),
	/** TRUE ⇒ more than {@link MAX_PROBLEM_WITHHELD} reasons matched; compared `> cap`. */
	truncated: z.boolean()
}).strict();
/**
* The `errorRate` section's data — ✅ **CONFIRMED by R-1(§7)**, and it is the CP
* tool surface for the same aggregate the `get_app_logs` `aggregate` arg returns.
* One shape, two consumers, imported not re-declared.
*
* ⛔ **There is no `lines` field and no `entries` field, structurally** (spine
* V-4). The whole point of the section is a NUMBER: the field report's complaint
* was a 143 KB response where one integer was wanted, so a shape that *can* carry
* payloads is a shape that eventually will.
*
* ⛔ **And there is no `truncated` field either.** R-1(3) makes this an INSTANT
* query of `sum(count_over_time(...))` — a vector result with no limit semantics,
* so there is no truncation concept to report. ⚠️ Do not add one "for
* consistency" with the other three sections: a `truncated: false` on a value
* that cannot be truncated is a claim about a mechanism that does not exist, and
* R-4's meaning ("more existed than you received") would be unfalsifiable here.
*
* ## ⭐ R-39.2 — `matchedStreams` is what stops this section BEING C-1
*
* ⛔ `total` is a scalar, and a scalar on a triage surface is one coercion away
* from being a verdict. `{total: 0}` with nothing beside it says *"your app was
* quiet for an hour"* — which is `get_app_status`' deleted sentence, re-derived.
* The imported {@link appLogAggregateSchema} therefore carries
* **`matchedStreams: boolean`**, and the service's `readErrorRate` turns `false`
* into a `read: false, reason: 'no_log_streams'` arm. ⇒ **On this section's
* `read: true` arm `matchedStreams` is ALWAYS `true`** — an invariant (I-25),
* pinned by test, not by hope. The field is still carried rather than stripped so
* that the ONE shape claim above ("one shape, two consumers, imported not
* re-declared") stays literally true; `get_app_logs`' aggregate mode has no `read`
* wrapper and needs the bit on the wire.
*/
const errorRateProblemDataSchema = appLogAggregateSchema;
const podsProblemSectionSchema = problemSection(podsProblemDataSchema);
const errorRateProblemSectionSchema = problemSection(errorRateProblemDataSchema);
const warningEventsProblemSectionSchema = problemSection(warningEventsProblemDataSchema);
const rolloutWithheldProblemSectionSchema = problemSection(rolloutWithheldProblemDataSchema);
/**
* One reconcile refusal, projected for an agent (compute-classes D7/D48(d)/D54).
*
* ⭐ PROJECTED, unlike `rolloutWithheld`'s items — and the asymmetry is deliberate. That section
* ships full `WithholdReason` rows because `remedy` + `detail` together ARE its product value.
* Here `remedy` carries the next action and `code` carries the machine-readable fact, while the
* refusal's free-text `detail` is left on the diagnostics surface: it embeds the app's own vector
* and ceiling numbers, and this response is capped for context, not for forensics.
* ⛔ `owner` and `remedy` are STAMPED BY THE PRODUCER from the total maps
* (`RECONCILE_REFUSAL_OWNER` / `RECONCILE_REFUSAL_REMEDY`) — never DP-asserted, never client-
* supplied. That is why `owner` needs a schema at all (D50(a)).
*/
const computeRefusalProblemItemSchema = z.object({
	/** The service or worker the refusal is about. */
	unit: z.string().min(1).max(253),
	/** The two namespaces are independent — a service and a worker may share a name. */
	unitKind: z.enum(["service", "worker"]),
	fixtureId: z.string().regex(FIXTURE_ID_PATTERN),
	code: reconcileRefusalCodeSchema,
	/** Always `RECONCILE_REFUSAL_OWNER[code]` — on the wire because an MCP agent holding proxied
	*  JSON has no access to the TypeScript map. This is D50(a)'s door. */
	owner: reconcileRefusalOwnerSchema,
	/** Always `RECONCILE_REFUSAL_REMEDY[code]`. The next action, for the same reason. */
	remedy: z.string().min(1),
	/** The machine type the resolver chose, or `null` when it refused before choosing. */
	resolvedNodeType: z.string().max(63).nullable()
}).strict();
/**
* The `computeRefusals` section's data.
*
* ⛔⛔ DO NOT ADD `total`, `count`, `severity` OR A SUMMARY FIELD HERE, however natural it feels.
* `ForbiddenSectionDataKey` forbids every verdict key at SECTION-DATA depth, and `total` is carved
* out ONLY for `errorRate.data`, where `matchedStreams` disarms it. The R-39.2 post-mortem is
* exactly this mistake: `{read: true, data: {total: 0}}` rendered *"your app was quiet"* one level
* down, inside the section built to replace the tool deleted for saying that. A refusal count of `0`
* would read as *"we checked and nothing is refused"* — which is precisely what `read: false` exists
* to distinguish.
*/
const computeRefusalsProblemDataSchema = z.object({
	items: z.array(computeRefusalProblemItemSchema),
	/** TRUE ⇒ more than {@link MAX_PROBLEM_COMPUTE_REFUSALS} refusals matched; compared `> cap`. */
	truncated: z.boolean()
}).strict();
/**
* ⛔ Built with THIS file's `problemSection`, **not** `app-diagnostics.ts`'s `sectionOf`. The two
* absence vocabularies are deliberately different and the docblock on `problemSection` records why:
* here `data` is ABSENT on the `read: false` arm, so a consumer cannot touch it without narrowing —
* the C-1 failure becomes a compile error rather than a discipline. Mixing the two would put a
* three-state and a two-state absence model in one response.
*/
const computeRefusalsProblemSectionSchema = problemSection(computeRefusalsProblemDataSchema);
/**
* `GET /v1/admin/apps/:appId/dps/:dpId/problems` — *"what is wrong with my app on
* this data plane, right now?"*
*
* Five independent producers, five independent failure modes, so each section
* carries its OWN provenance. A single top-level status would have to misreport at
* least one of them, which is exactly how C-1 happened.
*
* ⛔⛔ **THERE IS NO `health`, `ok`, `status`, `verdict`, `severity`, `summary`,
* `problemCount` OR `total` FIELD, AND THERE NEVER WILL BE.** `.strict()` makes
* adding one a parse failure at every consumer;
* {@link _appProblemsCarriesNoScalarVerdict} makes it a compile error; and
* `app-problems.spec.ts`' TEST-FENCE-1 makes it a red test naming this rule. All
* three exist because a `.strict()` schema alone stops an ACCIDENT and this needs
* to stop a well-intentioned edit.
*
* ## ⭐⭐ R-39.2 — THE RULE APPLIES AT **EVERY LEVEL**, NOT ONLY THIS ONE
*
* ⛔ **A value that FUNCTIONS as a verdict is a verdict wherever it sits.** The
* three fences above were all written against the TOP level, and B-2 walked
* straight past them: `errorRate.data` was `{total, byLevel?, window}`, so
* `{read: true, data: {total: 0}}` rendered *"your app was quiet"* one level
* down, inside the section built to replace the tool that was deleted for saying
* exactly that. The `problemCount` argument (*"a count is a scalar verdict by the
* back door"*) was right and was being applied one level too shallow.
*
* ⇒ **The rule, stated once and enforced at every depth:** no key in
* `ForbiddenVerdictKey` appears on the response OR on any section's `data`.
* TEST-FENCE-2 plants every forbidden key at BOTH depths, and
* {@link _appProblemsSectionDataCarriesNoScalarVerdict} is the compile-time half.
*
* ⛔ **The ONE carve-out, and it is carved out by a companion, not by exception.**
* `errorRate.data.total` is a legitimate counted quantity — it is the answer the
* section exists to give. What makes it not a verdict is that
* `matchedStreams: boolean` sits beside it and `readErrorRate` refuses to emit
* the `read: true` arm at all when it is `false` (R-39.2). ⇒ Deleting
* `matchedStreams` re-creates B-2; the carve-out is conditional on the companion
* and I-25 pins it. The other three sections' `data` are already `.strict()`
* `{items, truncated}`, so every forbidden key including `total` is rejected
* there by construction — the fence arm is the proof, not the mechanism.
*/
const appProblemsResponseSchema = z.object({
	/**
	* Echoes the `:appId` path param verbatim — the `release.apps.id` UUID, never
	* the slug. ⚠️ `z.uuid()`, the house form (`app-pods.ts:217,219`;
	* `app-read-tools.ts:76,99,113,115`). Under zod 4 `.string().uuid()` is
	* the deprecated spelling and two forms in one PR is drift.
	*/
	appId: z.uuid(),
	/** Echoes the `:dpId` path param verbatim. */
	dpId: z.uuid(),
	/** ISO-8601 — when this answer was computed. Same field, same meaning, as `AppDpDiagnostics`. */
	computedAt: z.string().datetime(),
	pods: podsProblemSectionSchema,
	/**
	* ✅ Confirmed by R-1(§7). Backed by WS-3's DP aggregate endpoint through the
	* `LOKI_PROXY_CLIENT` port's `aggregate()` method, consumed via
	* `AppLogsService` — ⛔ never by this service dialling the port itself, so the
	* app-scope selector composition and the `dpId` confinement stay in the one
	* service that owns them.
	*/
	errorRate: errorRateProblemSectionSchema,
	warningEvents: warningEventsProblemSectionSchema,
	rolloutWithheld: rolloutWithheldProblemSectionSchema,
	/**
	* compute-classes D7/D48(d) — reconcile refusals for this app on this DP. ⛔ OPTIONAL, unlike
	* its four siblings: they predate any deployed consumer, while this key lands on a `.strict()`
	* response already in flight, so a REQUIRED key would reject every older producer body and
	* every existing snapshot fixture. Same rule and same reason as `AppDpDiagnostics.compute?`.
	*/
	computeRefusals: computeRefusalsProblemSectionSchema.optional()
}).strict();
//#endregion
export { MAX_PROBLEM_COMPUTE_REFUSALS, MAX_PROBLEM_EVENTS, MAX_PROBLEM_PODS, MAX_PROBLEM_WITHHELD, PROBLEM_ERROR_RATE_WINDOW_MINUTES, PROBLEM_UNREAD_EXPLANATION, PROBLEM_UNREAD_REASONS, appProblemPodSchema, appProblemsResponseSchema, computeRefusalProblemItemSchema, computeRefusalsProblemDataSchema, computeRefusalsProblemSectionSchema, errorRateProblemDataSchema, errorRateProblemSectionSchema, podsProblemDataSchema, podsProblemSectionSchema, problemUnreadReasonSchema, rolloutWithheldProblemDataSchema, rolloutWithheldProblemSectionSchema, warningEventsProblemDataSchema, warningEventsProblemSectionSchema };

//# sourceMappingURL=app-problems.js.map